import { OwcListItem } from "@one/react";
import { fetchFilteredInstruments } from "../../redux/actions";

export const createModelSelectHandler = ({
  instrumentModels,
  setSelectedEquipment,
  formik,
  setInputValue,
  setModelSearchTerm,
}) => {
  return (e) => {
    if (e.detail !== null && e.detail !== undefined && e.detail !== "") {
      const selected = instrumentModels[e.detail];
      setSelectedEquipment(selected);
      const selectedValue =
        selected?.equipmentModel && selected.equipmentModel !== "null" ? selected.equipmentModel : selected?.serialNumber;
      formik.setFieldValue("model", selectedValue);
      setInputValue(selectedValue);
      return;
    }

    formik.setFieldValue("model", "");
    setInputValue("");
    setModelSearchTerm("");
    setSelectedEquipment(null);
  };
};

export const createModelInputChangeHandler = (ctx) => {
  const {
    isEditMode,
    isUpdatingModelOptionsRef,
    suppressSyntheticEmptyInputRef,
    setInputValue,
    setModelSearchTerm,
    formik,
    setSelectedEquipment,
  } = ctx;

  return (e) => {
    if (isEditMode) {
      return;
    }

    const searchValue = e.detail || "";
    if (searchValue === "" && (isUpdatingModelOptionsRef.current || suppressSyntheticEmptyInputRef.current)) {
      return;
    }

    setInputValue(searchValue);
    setModelSearchTerm(searchValue);

    if (searchValue === "") {
      formik.setFieldValue("model", "");
      setSelectedEquipment(null);
    }
  };
};

export const createModelClearHandler = ({ isEditMode, formik, setInputValue, setModelSearchTerm, setSelectedEquipment }) => {
  return () => {
    if (isEditMode) {
      return;
    }

    formik.setFieldValue("model", "");
    setInputValue("");
    setModelSearchTerm("");
    setSelectedEquipment(null);
  };
};

export const getInstrumentOptionValue = (item) => item?.equipmentModel || item?.serialNumber || "";

export const getInstrumentOptionLabel = (item) => {
  if (item?.equipmentModel && item.equipmentModel !== "null") {
    return item.equipmentModel;
  }
  return item?.serialNumber ? `SN: ${item.serialNumber}` : "";
};

export const getModelLabel = (isLoadingModels) => (isLoadingModels ? "Loading models..." : "Select model name *");

export const fetchInstrumentModels = async ({ client, user, dispatch, searchTerm }) => {
  if (!client || !user) {
    return;
  }

  try {
    const filters = searchTerm
      ? {
        equipmentModel: searchTerm.trim(),
      }
      : {};

    await dispatch(
      fetchFilteredInstruments({
        client,
        user,
        filters,
      })
    );
  } catch (error) {
    console.error("Error loading instrument models:", error);
  }
};

export const getSortedUniqueModels = (filteredInstruments) => {
  if (!filteredInstruments) {
    return null;
  }

  const uniqueModels = [];
  const modelMap = new Map();

  filteredInstruments.forEach((item) => {
    const modelKey = item.equipmentModel || item.serialNumber;
    if (modelKey && !modelMap.has(modelKey)) {
      modelMap.set(modelKey, item);
      uniqueModels.push(item);
    }
  });

  return uniqueModels.sort((a, b) => {
    const modelA = (a.equipmentModel || a.serialNumber || "").toLowerCase();
    const modelB = (b.equipmentModel || b.serialNumber || "").toLowerCase();
    return modelA.localeCompare(modelB);
  });
};

export const toNullableValue = (value) => value || null;

export const getModelFieldValue = ({ isEditMode, formModelValue, inputValue }) => {
  if (isEditMode) {
    return toNullableValue(formModelValue);
  }
  return toNullableValue(inputValue);
};

export const loadModelsOnDialogOpen = ({ dialogOpen, isEditMode, loadInstrumentModels, modelSearchTerm }) => {
  if (dialogOpen && !isEditMode) {
    loadInstrumentModels(modelSearchTerm);
  }
};

export const syncModelOptionsState = ({
  filteredInstruments,
  isUpdatingModelOptionsRef,
  suppressSyntheticEmptyInputRef,
  setInstrumentModels,
}) => {
  const sortedModels = getSortedUniqueModels(filteredInstruments);
  if (!sortedModels) {
    return;
  }

  // OwcEditableDropdown may emit a synthetic onInputChange("") when options update.
  // Mark this short window so that handler can ignore that synthetic empty event.
  isUpdatingModelOptionsRef.current = true;
  suppressSyntheticEmptyInputRef.current = true;
  setInstrumentModels(sortedModels);
  setTimeout(() => {
    isUpdatingModelOptionsRef.current = false;
  }, 200);
  setTimeout(() => {
    suppressSyntheticEmptyInputRef.current = false;
  }, 350);
};

export const syncModelWithFormikValue = ({ formik, instrumentModels, setInstrumentModels }) => {
  if (!formik.values.model) {
    return;
  }

  let matchedInstrument = instrumentModels.find(
    (instrument) => instrument.equipmentModel === formik.values.model || instrument.serialNumber === formik.values.model
  );
  if (!matchedInstrument) {
    const fallbackInstrument = {
      equipmentModel: formik.values.model,
      serialNumber: "",
    };
    matchedInstrument = fallbackInstrument;
    setInstrumentModels((prev) => [...prev, fallbackInstrument]);
  }

  formik.setFieldValue("model", matchedInstrument.equipmentModel || matchedInstrument.serialNumber);
};

export const refreshModelPopover = ({ isEditMode, dialogOpen, isLoadingModels, modelDropdownRef, instrumentModels }) => {
  if (isEditMode || !dialogOpen || isLoadingModels || !modelDropdownRef.current) {
    return undefined;
  }

  const timer = setTimeout(async () => {
    try {
      await modelDropdownRef.current?.updateList?.();
      const popover = await modelDropdownRef.current?.getPopoverElement?.();
      if (popover) {
        popover.style.display = instrumentModels.length > 0 ? "block" : "none";
      }
    } catch (error) {
      console.error("Error refreshing model dropdown popover:", error);
    }
  }, 0);

  return () => clearTimeout(timer);
};

export const renderModelDropdownItems = ({
  isLoadingModels,
  instrumentModels,
  modelSearchTerm,
}) => {
  if (isLoadingModels) {
    return (
      <OwcListItem value="" disabled>
        Loading...
      </OwcListItem>
    );
  }

  if (instrumentModels.length === 0) {
    return (
      <OwcListItem value="" disabled>
        {modelSearchTerm ? "No models found" : "Start typing to search"}
      </OwcListItem>
    );
  }

  return instrumentModels.map((item, index) => {
    const value = getInstrumentOptionValue(item);
    const label = getInstrumentOptionLabel(item);

    return (
      <OwcListItem key={`${item.inventoryId || index}`} value={value}>
        {label || "(EMPTY LABEL)"}
      </OwcListItem>
    );
  });
};
